import { Activity, ActivityCategory } from '@/types';
import { getAllActivitiesForSearch } from '@/services/activityService';

/**
 * Debounce function to limit the rate of function execution
 * @param fn - Function to debounce
 * @param delay - Delay in milliseconds
 * @returns Debounced function
 */
export function debounce<T extends (...args: any[]) => any>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;
  
  return function (this: any, ...args: Parameters<T>) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn.apply(this, args), delay);
  };
}

/**
 * Get activity suggestions based on search query
 * Searches through activity names, categories, and short descriptions
 * @param query - Search query string
 * @param limit - Maximum number of suggestions to return (default: 5)
 * @returns Array of matching activities
 */
export async function getActivitySuggestions(query: string, limit: number = 5): Promise<Activity[]> {
  if (!query || query.trim().length < 2) {
    return [];
  }

  const lowerQuery = query.toLowerCase().trim();
  const activities = await getAllActivitiesForSearch();

  const matches = activities.filter((activity) => {
    const nameMatch = activity.name?.toLowerCase().includes(lowerQuery);
    const descMatch = activity.shortDescription?.toLowerCase().includes(lowerQuery);
    const venueDescMatch = activity.venueShortDescription?.toLowerCase().includes(lowerQuery) ?? false;
    return nameMatch || descMatch || venueDescMatch;
  });

  return matches.slice(0, limit);
}

/**
 * Get category suggestions based on search query
 * @param query - Search query string
 * @returns Array of matching category values
 */
export function getCategorySuggestions(query: string): ActivityCategory[] {
  if (!query || query.trim().length < 2) {
    return [];
  }

  const lowerQuery = query.toLowerCase().trim();
  
  const categoryMap: Record<ActivityCategory, string> = {
    'bowling': 'Bowling',
    'karting': 'Karting',
    'escape-game': 'Escape Game',
    'laser-game': 'Laser Game',
    'padel': 'Padel',
    'foot-indoor': 'Foot Indoor',
    'trampoline-park': 'Trampoline Park',
    'billard': 'Billard',
    'paintball-indoor': 'Paintball Indoor',
    'vr-arcade': 'VR Arcade'
  };

  const matches: ActivityCategory[] = [];

  for (const [category, label] of Object.entries(categoryMap)) {
    if (
      label.toLowerCase().includes(lowerQuery) ||
      category.toLowerCase().includes(lowerQuery)
    ) {
      matches.push(category as ActivityCategory);
    }
  }

  return matches;
}

/**
 * Get the display label for a category
 * @param category - Activity category
 * @returns Display label for the category
 */
export function getCategoryLabel(category: ActivityCategory): string {
  const categoryLabels: Record<ActivityCategory, string> = {
    'bowling': 'Bowling',
    'karting': 'Karting',
    'escape-game': 'Escape Game',
    'laser-game': 'Laser Game',
    'padel': 'Padel',
    'foot-indoor': 'Foot Indoor',
    'trampoline-park': 'Trampoline Park',
    'billard': 'Billard',
    'paintball-indoor': 'Paintball Indoor',
    'vr-arcade': 'VR Arcade',
    'autre': 'Autre',
  };

  return categoryLabels[category] || category;
}

/**
 * Highlight matched text in a string
 * @param text - Original text
 * @param query - Search query to highlight
 * @returns Object with parts array containing text segments and whether they match
 */
export function highlightMatch(text: string, query: string): { parts: Array<{ text: string; match: boolean }> } {
  if (!query || !text) {
    return { parts: [{ text, match: false }] };
  }

  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const index = lowerText.indexOf(lowerQuery);

  if (index === -1) {
    return { parts: [{ text, match: false }] };
  }

  const parts = [];
  
  if (index > 0) {
    parts.push({ text: text.substring(0, index), match: false });
  }
  
  parts.push({ text: text.substring(index, index + query.length), match: true });
  
  if (index + query.length < text.length) {
    parts.push({ text: text.substring(index + query.length), match: false });
  }

  return { parts };
}

